import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { fileSizeLabel, type ChatAttachmentDto } from '@taqon/contracts';
import { DOCUMENTS } from '../../shared/documents/DocumentsPanel';

const isAudio = (contentType: string) => contentType.startsWith('audio/');

/**
 * A message's attachments in place (FRD-CORE-050, CORE-067): a voice message plays here, a file
 * downloads. Each is the document subsystem's: only a scanned, clean file opens (SDD §17.5).
 */
export function MessageAttachments({ attachments }: { attachments: readonly ChatAttachmentDto[] }) {
  if (attachments.length === 0) return null;
  return (
    <Stack component="ul" spacing={0.5} sx={{ listStyle: 'none', p: 0, m: 0, mt: 0.75 }}>
      {attachments.map((attachment) => (
        <Box component="li" key={attachment.documentId}>
          <AttachmentItem attachment={attachment} />
        </Box>
      ))}
    </Stack>
  );
}

function AttachmentItem({ attachment }: { attachment: ChatAttachmentDto }) {
  const audio = isAudio(attachment.contentType);
  const name = audio ? attachment.title : attachment.fileName;
  if (attachment.status !== 'available') {
    return (
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
        {audio ? (
          <GraphicEqIcon fontSize="small" color="disabled" />
        ) : (
          <AttachFileIcon fontSize="small" color="disabled" />
        )}
        <Typography variant="body2" color="text.secondary">
          {name} —{' '}
          {attachment.status === 'pending_scan'
            ? 'being checked for viruses; it opens once the check is clean'
            : 'failed the virus check and cannot be opened'}
        </Typography>
      </Stack>
    );
  }
  if (audio) return <VoicePlayer attachment={attachment} />;
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
      <AttachFileIcon fontSize="small" color="action" />
      <Link
        href={`${DOCUMENTS}/${attachment.documentId}/content`}
        variant="body2"
        sx={{ overflowWrap: 'anywhere' }}
      >
        {attachment.fileName}
      </Link>
      <Typography variant="caption" color="text.secondary">
        {fileSizeLabel(attachment.sizeBytes)}
      </Typography>
    </Stack>
  );
}

/**
 * Plays a voice message. Nothing is fetched until Play: the recording is then read once (an audited
 * access, FRD-CORE-022) and played from memory, which every browser can seek in.
 */
function VoicePlayer({ attachment }: { attachment: ChatAttachmentDto }) {
  const [source, setSource] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(
    () => () => {
      if (source !== null) URL.revokeObjectURL(source);
    },
    [source],
  );

  const load = async () => {
    setLoading(true);
    setFailed(false);
    try {
      const response = await fetch(`${DOCUMENTS}/${attachment.documentId}/content`, {
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(String(response.status));
      setSource(URL.createObjectURL(await response.blob()));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  if (source !== null) {
    return (
      <Box
        component="audio"
        controls
        autoPlay
        src={source}
        aria-label={attachment.title}
        sx={{ display: 'block', width: '100%', maxWidth: 360, height: 40 }}
      />
    );
  }
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <Button
        size="small"
        variant="outlined"
        startIcon={<PlayArrowIcon />}
        disabled={loading}
        onClick={() => void load()}
        aria-label={`Play ${attachment.title}`}
      >
        {loading ? 'Loading…' : attachment.title}
      </Button>
      {failed && (
        <Typography variant="caption" color="error" role="alert">
          The voice message could not be loaded. Try again.
        </Typography>
      )}
    </Stack>
  );
}

/** A recording not yet sent, played from the browser's memory. */
export function LocalAudio({ file, label }: { file: File; label: string }) {
  const player = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    if (player.current !== null) player.current.src = url;
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);
  return (
    <Box
      component="audio"
      ref={player}
      controls
      aria-label={label}
      sx={{ display: 'block', width: '100%', maxWidth: 300, height: 36 }}
    />
  );
}
